import { Directive, ElementRef, inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyTooltip as MatTooltip } from '@angular/material/legacy-tooltip';

/** Allowance for layout rounding when comparing the text width with the box width. */
const SUB_PIXEL_TOLERANCE = 0.02;

/**
 * Shows the element's full text in its Material tooltip while that text is clipped.
 * Must sit on an element that also carries `matTooltip`.
 */
@Directive({
    selector: '[appOverflowTooltip]',
    standalone: true,
})
export class OverflowTooltipDirective implements OnInit, OnDestroy {
    private readonly element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    private readonly tooltip = inject(MatTooltip, { self: true });
    private readonly ngZone = inject(NgZone);

    ngOnInit(): void {
        this.ngZone.runOutsideAngular(() => this.element.addEventListener('mouseover', this.onMouseOver));
    }

    ngOnDestroy(): void {
        this.element.removeEventListener('mouseover', this.onMouseOver);
    }

    /** Re-measures on every hover and re-enters the Angular zone only when the tooltip text changes. */
    private readonly onMouseOver = (): void => {
        const message = this.isClipped() ? (this.element.textContent?.trim() ?? '') : '';
        if (message !== this.tooltip.message) {
            this.ngZone.run(() => {
                this.tooltip.message = message;
            });
        }
    };

    /** True when the text is wider than its box, including by the fraction of a pixel that scrollWidth rounds away. */
    private isClipped(): boolean {
        const range = this.element.ownerDocument.createRange();
        range.selectNodeContents(this.element);
        const overflow = range.getBoundingClientRect().width - this.element.getBoundingClientRect().width;
        return this.element.scrollWidth > this.element.clientWidth || overflow > SUB_PIXEL_TOLERANCE;
    }
}
